import { computed } from 'vue';
import { useLocalPreferencesStore } from '@/stores/localPreferences';
import { globalRouterReference } from '@/router/globalRouterReference';

// Public setup route (`p-document-create`) hosts 3rd-party/ERP documents that
// were never registered in the local preferences, so wizard completion can't be
// tracked there the usual way. We persist it per-document in its own
// localStorage key instead: this survives a page refresh, so once the user has
// completed or dismissed the wizard, reopening a summary card goes straight to
// that section (and the wizard no longer auto-opens) rather than replaying the
// full stepper.
const PUBLIC_COMPLETED_STORAGE_KEY = 'deepsign.publicWizardCompleted';

// Nothing ever removes an id from the public list (there is no document
// lifecycle to hook into on that route), so keep only the most recent ones.
const PUBLIC_COMPLETED_LIMIT = 50;

function readPublicCompleted(): string[] {
  try {
    const raw = localStorage.getItem(PUBLIC_COMPLETED_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function isPublicCompleted(documentId: string): boolean {
  return readPublicCompleted().includes(documentId);
}

function markPublicCompleted(documentId: string): void {
  const list = readPublicCompleted();
  if (list.includes(documentId)) return;
  try {
    localStorage.setItem(
      PUBLIC_COMPLETED_STORAGE_KEY,
      JSON.stringify([...list, documentId].slice(-PUBLIC_COMPLETED_LIMIT))
    );
  } catch {
    // Storage unavailable (private mode / quota); degrade to session-only.
  }
}

/**
 * Per-document "setup wizard" state, kept in the device-local preferences (it
 * used to live on the capped user profile payload — see localPreferences). The
 * signing-workflow dialog shows the full stepper wizard the first time a
 * document is set up; once completed, each summary card opens only its own
 * section.
 *
 * `documentsWizards` is a string[] of pending document ids only — presence
 * means the wizard is still open; absence means completed, dismissed, or never
 * registered.
 *
 * Lifecycle: `registerDocument` appends the id on creation. Confirm, dismiss,
 * start (single or batch), and delete all drop it via `completeWizard` /
 * `removeDocument(s)`.
 */
export function useDocumentWizard() {
  const localPreferencesStore = useLocalPreferencesStore();

  // Read the route off the global router reference (not useRoute()) so this
  // composable stays safe when instantiated outside a component setup context —
  // e.g. via useInviteesActions() inside the create Pinia store.
  const isPublicRoute = () =>
    globalRouterReference.router?.currentRoute.value.name ===
    'p-document-create';

  const wizards = computed<string[]>(
    () => localPreferencesStore.documentsWizards
  );

  // Show the stepper wizard while setup is pending: on the public route until
  // the localStorage completion flag is set, on the dashboard while the id is
  // in the pending list. An absent id uses per-section editing, so legacy
  // documents aren't forced back through the wizard.
  function isWizardMode(documentId: string): boolean {
    if (isPublicRoute()) return !isPublicCompleted(documentId);
    return wizards.value.includes(documentId);
  }

  // Auto-open the full wizard once, while setup is still pending. On the public
  // route "pending" is the absence of the localStorage completion flag; on the
  // dashboard it's the document id in the pending list.
  function shouldAutoOpen(documentId: string): boolean {
    if (isPublicRoute()) return !isPublicCompleted(documentId);
    return wizards.value.includes(documentId);
  }

  function registerDocument(documentId: string | undefined) {
    // Skip on the public route — there the dialog tracks completion in its own
    // localStorage key and always falls back to wizard mode anyway.
    if (!documentId || isPublicRoute() || wizards.value.includes(documentId)) {
      return;
    }
    localPreferencesStore.documentsWizards = [...wizards.value, documentId];
  }

  function completeWizard(documentId: string) {
    // Public route: no pending entry exists; record completion in localStorage
    // so future opens skip the stepper and survive a refresh.
    if (isPublicRoute()) {
      markPublicCompleted(documentId);
      return;
    }
    removeDocuments([documentId]);
  }

  function removeDocument(documentId: string) {
    removeDocuments([documentId]);
  }

  function removeDocuments(documentIds: string[]) {
    const ids = new Set(documentIds);
    if (!wizards.value.some((id) => ids.has(id))) return;
    localPreferencesStore.documentsWizards = wizards.value.filter(
      (id) => !ids.has(id)
    );
  }

  return {
    wizards,
    isWizardMode,
    shouldAutoOpen,
    registerDocument,
    completeWizard,
    removeDocument,
    removeDocuments
  };
}
